<template>
  <section class="page-shell document-generator-page">
    <div class="page-heading">
      <div>
        <h1>Gerar documento</h1>
        <p>Crie documentos estruturados e siga direto para validação, revisão humana e evidência.</p>
      </div>
    </div>

    <div class="document-generator-flow">
      <section class="data-panel generation-input-panel">
        <div class="panel-kicker">Documento assistido</div>
        <h2>Contexto do documento</h2>
        <v-alert v-if="contextMessage" type="info" dense text class="document-context-alert">
          {{ contextMessage }}
        </v-alert>

        <div class="template-row">
          <button
            v-for="template in templates"
            :key="template.type"
            type="button"
            class="template-option"
            :class="{ 'template-option--active': form.document_type === template.type }"
            @click="applyTemplate(template)"
          >
            <v-icon>{{ template.icon }}</v-icon>
            <strong>{{ template.type }}</strong>
            <span>{{ template.hint }}</span>
          </button>
        </div>

        <div class="form-grid">
          <v-select v-model="form.document_type" :items="documentTypes" label="Tipo de documento" outlined dense hide-details />
          <v-select v-model="form.destination" :items="destinations" label="Destino" outlined dense hide-details />
        </div>

        <div class="form-grid">
          <v-text-field v-model="form.client_name" label="Cliente" outlined dense hide-details />
          <v-text-field v-model="form.matter_reference" label="Caso/processo/contrato" outlined dense hide-details />
        </div>

        <div class="form-grid">
          <v-select v-model="form.tone" :items="tones" label="Tom" outlined dense hide-details />
          <v-text-field
            v-model="form.purpose"
            label="Objetivo do documento"
            outlined
            dense
            :error-messages="purposeErrorMessages"
          />
        </div>

        <v-textarea
          v-model="form.facts"
          label="Fatos principais"
          hint="Descreva os fatos com pelo menos 20 caracteres para gerar o documento."
          persistent-hint
          counter
          :error-messages="factsErrorMessages"
          outlined
          rows="7"
          no-resize
        />

        <v-textarea
          v-model="form.instructions"
          label="Instruções adicionais"
          outlined
          rows="4"
          hide-details
          no-resize
        />

        <div class="professional-profile-card">
          <div>
            <span>Preenchimento automático</span>
            <strong>Usar dados da minha conta</strong>
            <p>{{ professionalProfileText }}</p>
          </div>
          <v-switch
            v-model="useProfessionalProfile"
            color="primary"
            inset
            hide-details
            :disabled="!professionalProfileAvailable"
          />
          <v-btn
            v-if="!professionalProfileAvailable"
            small
            outlined
            color="primary"
            :to="{ name: 'my-account' }"
          >
            Completar cadastro
          </v-btn>
        </div>

        <div
          class="document-dropzone document-dropzone--compact"
          :class="{ 'document-dropzone--dragging': isDraggingFile, 'document-dropzone--filled': baseFile }"
          @click="$refs.baseFileInput.click()"
          @dragover.prevent="isDraggingFile = true"
          @dragleave.prevent="isDraggingFile = false"
          @drop.prevent="handleFileDrop"
        >
          <input
            ref="baseFileInput"
            type="file"
            accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            class="document-dropzone-input"
            @change="handleFileSelect"
          >
          <v-icon>{{ baseFile ? 'mdi-file-check-outline' : 'mdi-paperclip' }}</v-icon>
          <div>
            <strong>{{ baseFile ? baseFile.name : 'Documento base opcional' }}</strong>
            <p>{{ baseFile ? fileSize(baseFile.size) : 'Use PDF, DOCX ou TXT apenas se quiser aproveitar uma base existente.' }}</p>
          </div>
          <v-btn v-if="baseFile" small text color="error" @click.stop="clearBaseFile">Remover</v-btn>
        </div>

        <div class="generation-credit-estimate">
          <div>
            <span>Consumo estimado</span>
            <strong>{{ generationCreditEstimate }} {{ generationCreditEstimate === 1 ? 'crédito' : 'créditos' }}</strong>
          </div>
          <p>{{ generationCreditLabel }}. O consumo final é confirmado ao gerar o documento.</p>
        </div>

        <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
        <v-btn block color="primary" height="44" :loading="loading" @click="generateDocument">
          Gerar documento
        </v-btn>
      </section>

      <section v-if="result" ref="generatedResultPanel" class="data-panel generation-result-panel">
        <div class="result-header">
          <div>
            <div class="panel-kicker">Documento gerado</div>
            <h2>{{ result.generation.title }}</h2>
          </div>
          <v-chip v-if="result.evidence" color="primary" text-color="white">
            Evidência #{{ result.evidence.id }}
          </v-chip>
          <v-chip v-if="result.credit_estimate" color="secondary" outlined>
            {{ result.credit_estimate.credits }} {{ result.credit_estimate.credits === 1 ? 'crédito consumido' : 'créditos consumidos' }}
          </v-chip>
        </div>

        <document-editor-workspace
          ref="generatedDocumentEditor"
          v-model="editorText"
          class="generated-preview-editor"
          :comments="generatedComments"
          :layout-settings="editorLayoutSettings"
          :cover-title="result.generation.title"
          title="Editor da minuta"
          helper="Edite texto, fonte, estilos e layout antes de validar ou enviar para revisão."
          @update:comments="generatedComments = $event"
          @update:layout-settings="editorLayoutSettings = $event"
        />

        <div class="generation-notes" v-if="hasNotes">
          <article v-if="result.generation.missing_information.length">
            <strong>Informações pendentes</strong>
            <ul>
              <li v-for="item in result.generation.missing_information" :key="`missing-${item}`">{{ item }}</li>
            </ul>
          </article>
          <article v-if="result.generation.validation_notes.length">
            <strong>Pontos para validação</strong>
            <ul>
              <li v-for="item in result.generation.validation_notes" :key="`note-${item}`">{{ item }}</li>
            </ul>
          </article>
          <article v-if="result.generation.assumptions.length">
            <strong>Premissas usadas</strong>
            <ul>
              <li v-for="item in result.generation.assumptions" :key="`assumption-${item}`">{{ item }}</li>
            </ul>
          </article>
        </div>

        <div class="result-actions">
          <v-alert v-if="reviewMessage" type="success" dense text>{{ reviewMessage }}</v-alert>
          <v-btn outlined color="primary" @click="copyDocument">
            <v-icon left small>mdi-content-copy</v-icon>
            Copiar
          </v-btn>
          <v-btn outlined color="primary" @click="openDocumentDrive">
            <v-icon left small>mdi-folder-open-outline</v-icon>
            Abrir no editor
          </v-btn>
          <v-btn outlined color="primary" :loading="savingDocument" @click="saveGeneratedDocument">
            <v-icon left small>mdi-content-save-outline</v-icon>
            Salvar alterações
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn outlined color="primary" :disabled="!result || !result.document" :loading="exportLoading" v-bind="attrs" v-on="on">
                <v-icon left small>mdi-download-outline</v-icon>
                Exportar
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item v-for="option in exportOptions" :key="option.value" @click="downloadGeneratedDocument(option.value)">
                <v-list-item-icon>
                  <v-icon small>{{ option.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ option.text }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn color="primary" @click="validateGeneratedDocument">
            <v-icon left small>mdi-file-search-outline</v-icon>
            Validar documento
          </v-btn>
          <v-btn outlined color="primary" :loading="reviewLoading" @click="openHumanReviewDialog">
            <v-icon left small>mdi-account-check-outline</v-icon>
            Enviar para revisão humana
          </v-btn>
        </div>
      </section>
    </div>

    <v-dialog v-model="reviewDialog" max-width="520">
      <v-card>
        <v-card-title>Enviar para revisão humana</v-card-title>
        <v-card-text>
          <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
          <v-select
            v-model="selectedReviewer"
            :items="reviewerOptions"
            :loading="reviewersLoading"
            label="Responsável pela revisão"
            outlined
            dense
            hide-details
            no-data-text="Nenhum funcionário ativo encontrado"
          />
          <p class="dialog-helper">
            O documento gerado será enviado com o texto atual do editor.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="reviewDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="reviewLoading" @click="sendToHumanReview">Enviar para revisão</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from '../../plugins/axios'
import DocumentEditorWorkspace from '../../components/DocumentEditorWorkspace.vue'

const validatorDraftKey = 'complyops:document-validator-draft'
const documentContextDraftKey = 'complyops:document-generator-context'

const emptyForm = () => ({
  document_type: 'Petição',
  purpose: '',
  client_name: '',
  matter_reference: '',
  destination: 'Uso interno',
  tone: 'Técnico e objetivo',
  facts: '',
  instructions: ''
})

export default {
  name: 'DocumentGeneratorPage',
  components: {
    DocumentEditorWorkspace
  },
  data () {
    return {
      form: emptyForm(),
      result: null,
      editorText: '',
      editorLayoutSettings: this.defaultLayoutSettings(),
      generatedComments: [],
      submitAttempted: false,
      factsMinLength: 20,
      loading: false,
      savingDocument: false,
      exportLoading: false,
      reviewLoading: false,
      error: '',
      reviewMessage: '',
      contextMessage: '',
      reviewDialog: false,
      reviewersLoading: false,
      employees: [],
      selectedReviewer: '',
      useProfessionalProfile: true,
      baseFile: null,
      isDraggingFile: false,
      documentTypes: ['Minuta', 'Petição', 'Minuta contratual', 'Notificação extrajudicial', 'Parecer interno', 'E-mail para cliente', 'Cláusula contratual', 'Resumo jurídico'],
      destinations: ['Uso interno', 'Enviar para cliente', 'Enviar para contraparte', 'Protocolar em processo', 'Assinatura'],
      tones: ['Técnico e objetivo', 'Formal', 'Didático', 'Negocial', 'Preventivo'],
      templates: [
        { type: 'Minuta', icon: 'mdi-file-document-edit-outline', hint: 'Documento preliminar' },
        { type: 'Petição', icon: 'mdi-gavel', hint: 'Pedidos, fatos e fundamentos' },
        { type: 'Notificação extrajudicial', icon: 'mdi-email-edit-outline', hint: 'Comunicação formal' },
        { type: 'E-mail para cliente', icon: 'mdi-email-outline', hint: 'Resposta revisável' }
      ],
      exportOptions: [
        { text: 'PDF', value: 'pdf', icon: 'mdi-file-pdf-box' },
        { text: 'Word (.docx)', value: 'docx', icon: 'mdi-file-word-box' },
        { text: 'Texto (.txt)', value: 'txt', icon: 'mdi-file-document-outline' }
      ]
    }
  },
  computed: {
    currentUser () {
      return this.$store.state.auth.user || {}
    },
    professionalProfileAvailable () {
      return Boolean((this.currentUser.professional_full_name || this.currentUser.name) && this.currentUser.oab_number && this.currentUser.oab_section)
    },
    professionalProfileText () {
      if (!this.professionalProfileAvailable) {
        return 'Informe nome profissional e OAB em Minha conta para preencher assinatura e qualificação automaticamente.'
      }

      const name = this.currentUser.professional_full_name || this.currentUser.name
      const title = this.currentUser.professional_title || 'Advogado responsável'
      return `${name} · ${title} · OAB/${this.currentUser.oab_section} ${this.currentUser.oab_number}`
    },
    hasNotes () {
      return this.result && (
        this.result.generation.missing_information.length ||
        this.result.generation.validation_notes.length ||
        this.result.generation.assumptions.length
      )
    },
    wordCount () {
      return String(this.editorText || '').trim().split(/\s+/).filter(Boolean).length
    },
    factsLength () {
      return String(this.form.facts || '').trim().length
    },
    generationInputCharacterEstimate () {
      const structuredText = [
        this.form.document_type,
        this.form.purpose,
        this.form.client_name,
        this.form.matter_reference,
        this.form.destination,
        this.form.tone,
        this.form.facts,
        this.form.instructions
      ].join(' ')

      return structuredText.length + this.estimatedBaseFileCharacters
    },
    estimatedBaseFileCharacters () {
      if (!this.baseFile) return 0
      return Math.min(Math.ceil(Number(this.baseFile.size || 0) / 2), 25000)
    },
    generationCreditEstimate () {
      return this.creditsForCharacters(this.generationInputCharacterEstimate)
    },
    generationCreditLabel () {
      return {
        1: 'Documento curto',
        2: 'Documento médio',
        5: 'Documento longo',
        10: 'Documento extremo',
        20: 'Documento avançado',
        30: 'Documento crítico'
      }[this.generationCreditEstimate] || 'Documento'
    },
    factsErrorMessages () {
      if (!this.submitAttempted && !this.form.facts) return []
      if (this.factsLength < this.factsMinLength) {
        return [`Informe pelo menos ${this.factsMinLength} caracteres em fatos principais.`]
      }
      return []
    },
    purposeErrorMessages () {
      if (!this.submitAttempted || String(this.form.purpose || '').trim()) return []
      return ['Informe o objetivo do documento.']
    },
    reviewerOptions () {
      return this.employees
        .filter(employee => employee.access_status === 'active' && employee.user_status === 'active')
        .map(employee => ({
          text: `${employee.name || employee.email} · ${employee.role_name || 'Sem papel'}`,
          value: this.reviewerValue(employee)
        }))
    }
  },
  created () {
    this.loadImportedContext()
    this.loadReviewers()
    this.loadMyProfile()
  },
  methods: {
    loadImportedContext () {
      const rawDraft = localStorage.getItem(documentContextDraftKey)
      if (!rawDraft) return

      try {
        const draft = JSON.parse(rawDraft)
        this.applyImportedContext(draft)
        localStorage.removeItem(documentContextDraftKey)
      } catch (error) {
        localStorage.removeItem(documentContextDraftKey)
      }
    },
    applyImportedContext (draft) {
      if (!draft || draft.source !== 'datajud') return

      this.form = {
        ...this.form,
        document_type: draft.document_type || this.form.document_type,
        purpose: draft.purpose || this.form.purpose,
        client_name: draft.client_name || this.form.client_name,
        matter_reference: draft.matter_reference || this.form.matter_reference,
        destination: draft.destination || this.form.destination,
        tone: draft.tone || this.form.tone,
        facts: draft.facts || this.form.facts,
        instructions: draft.instructions || this.form.instructions
      }
      this.contextMessage = `${draft.source_label || 'Contexto externo'} aplicado como base para o documento. Revise e complemente os fatos antes de gerar.`
    },
    applyTemplate (template) {
      this.form.document_type = template.type
      if (!this.form.purpose) {
        this.form.purpose = this.defaultPurpose(template.type)
      }
    },
    defaultPurpose (type) {
      return {
        Minuta: 'Criar documento preliminar para revisão',
        Petição: 'Preparar documento para revisão antes do protocolo',
        'Notificação extrajudicial': 'Formalizar uma comunicação preventiva',
        'Parecer interno': 'Organizar análise para decisão do responsável',
        'E-mail para cliente': 'Responder ao cliente com linguagem clara e segura'
      }[type] || ''
    },
    async generateDocument () {
      this.submitAttempted = true
      this.error = ''
      this.reviewMessage = ''
      if (!this.validateGenerationForm()) return

      this.loading = true

      try {
        const payload = new FormData()
        Object.keys(this.form).forEach(key => {
          payload.append(key, this.form[key] || '')
        })
        payload.append('use_professional_profile', this.useProfessionalProfile && this.professionalProfileAvailable ? 'true' : 'false')

        if (this.baseFile) {
          payload.append('document_file', this.baseFile)
        }

        const { data } = await api.post('/document-generations/generate', payload)
        this.result = data
        this.editorText = data.generation.generated_text || ''
        this.editorLayoutSettings = this.normalizeLayoutSettings(data.document ? data.document.layout_settings : null, {
          title: data.generation.title,
          client_name: this.form.client_name,
          matter_reference: this.form.matter_reference,
          author: this.professionalProfileAvailable ? (this.currentUser.professional_full_name || this.currentUser.name) : ''
        })
        this.generatedComments = []
        this.$nextTick(() => {
          this.renderGeneratedEditor()
          this.scrollToGeneratedDocument()
        })
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : null
        this.error = data ? data.message : 'Não foi possível gerar o documento.'
      } finally {
        this.loading = false
      }
    },
    async validateGeneratedDocument () {
      this.syncGeneratedEditor()
      if (!this.editorText.trim()) {
        this.error = 'Gere ou escreva o documento antes de validar.'
        return
      }
      if (this.result && this.result.document) {
        await this.saveGeneratedDocument()
        if (this.error) return
      }

      localStorage.setItem(validatorDraftKey, JSON.stringify({
        document_type: this.form.document_type,
        client_name: this.form.client_name,
        matter_reference: this.form.matter_reference,
        intended_use: this.form.destination,
        document_text: this.editorText,
        source: 'document_generation',
        evidence_id: this.result && this.result.evidence ? this.result.evidence.id : null,
        generated_document_id: this.result && this.result.document ? this.result.document.id : null
      }))

      this.$router.push({ name: 'document-validator' })
    },
    validateGenerationForm () {
      if (!this.form.document_type || !String(this.form.purpose || '').trim()) {
        this.error = 'Informe o tipo de documento e o objetivo antes de gerar.'
        return false
      }

      if (this.factsLength < this.factsMinLength) {
        this.error = `Fatos principais precisa ter pelo menos ${this.factsMinLength} caracteres.`
        return false
      }

      return true
    },
    creditsForCharacters (characters) {
      const total = Number(characters || 0)
      const destination = String(this.form.destination || '').toLowerCase()
      const documentType = String(this.form.document_type || '').toLowerCase()
      const highResponsibility = destination.includes('protocolar') || destination.includes('assinatura')
      const complexType = documentType.includes('petição') || documentType.includes('contrat')
      let credits = 1

      if (total > 60000) {
        credits = 20
      } else if (total > 40000) {
        credits = 10
      } else if (total > 15000) {
        credits = 5
      } else if (total > 5000) {
        credits = 2
      }

      if (highResponsibility && credits < 5) credits = 5
      if (complexType && total > 15000 && credits < 10) credits = 10
      if (highResponsibility && total > 40000) credits = 30

      return credits
    },
    async copyDocument () {
      this.syncGeneratedEditor()
      try {
        await navigator.clipboard.writeText(this.editorText)
        this.reviewMessage = 'Documento copiado.'
      } catch (error) {
        this.error = 'Não foi possível copiar automaticamente.'
      }
    },
    renderGeneratedEditor () {
      if (this.$refs.generatedDocumentEditor) this.$refs.generatedDocumentEditor.renderEditorText()
    },
    scrollToGeneratedDocument () {
      const resultPanel = this.$refs.generatedResultPanel
      if (!resultPanel || !resultPanel.scrollIntoView) return
      resultPanel.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    syncGeneratedEditor () {
      if (this.$refs.generatedDocumentEditor) this.$refs.generatedDocumentEditor.syncEditorText()
    },
    async saveGeneratedDocument () {
      this.syncGeneratedEditor()
      if (!this.result || !this.result.document) {
        this.error = 'Gere o documento antes de salvar alterações.'
        return
      }
      this.savingDocument = true
      this.error = ''
      this.reviewMessage = ''
      try {
        const { data } = await api.put(`/generated-documents/${this.result.document.id}`, {
          title: this.result.generation.title,
          document_type: this.form.document_type,
          destination: this.form.destination,
          client_name: this.form.client_name,
          matter_reference: this.form.matter_reference,
          document_text: this.editorText,
          comments: this.generatedComments,
          layout_settings: this.editorLayoutSettings
        })
        this.result = {
          ...this.result,
          document: data
        }
        this.generatedComments = this.normalizeComments(data.comments)
        this.editorLayoutSettings = this.normalizeLayoutSettings(data.layout_settings)
        this.reviewMessage = 'Alterações salvas no documento.'
      } catch (error) {
        this.error = 'Não foi possível salvar as alterações.'
      } finally {
        this.savingDocument = false
      }
    },
    async downloadGeneratedDocument (format = 'pdf') {
      if (!this.result || !this.result.document) {
        this.error = 'Gere o documento antes de exportar.'
        return
      }

      this.exportLoading = true
      this.error = ''
      try {
        await this.saveGeneratedDocument()
        if (this.error) return

        const { data } = await api.get(`/generated-documents/${this.result.document.id}/download`, {
          params: { format },
          responseType: 'blob'
        })
        const url = window.URL.createObjectURL(data)
        const link = window.document.createElement('a')
        link.href = url
        link.download = `${this.slug(this.result.generation.title || 'documento')}.${format}`
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.error = 'Não foi possível exportar este documento.'
      } finally {
        this.exportLoading = false
      }
    },
    slug (value) {
      return String(value || 'documento')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'documento'
    },
    normalizeComments (comments) {
      if (Array.isArray(comments)) return comments
      if (!comments) return []
      try {
        return JSON.parse(comments) || []
      } catch (error) {
        return []
      }
    },
    defaultLayoutSettings () {
      return {
        font_family: 'Georgia',
        font_size: 15,
        line_height: 1.65,
        text_align: 'left',
        text_color: '#17211f',
        highlight_color: '#fff0b3',
        cover: { enabled: false, title: '', subtitle: '', client_name: '', matter_reference: '', author: '', date: '' },
        header: { enabled: false, text: '' },
        footer: { enabled: false, text: '', show_page_number: false },
        watermark: { enabled: false, text: '', opacity: 0.08 },
        logo: { enabled: false, url: '' }
      }
    },
    normalizeLayoutSettings (settings, context = {}) {
      const defaults = this.defaultLayoutSettings()
      let value = settings || {}
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value) || {}
        } catch (error) {
          value = {}
        }
      }
      return {
        ...defaults,
        ...value,
        cover: {
          ...defaults.cover,
          ...(value.cover || {}),
          title: (value.cover && value.cover.title) || context.title || defaults.cover.title,
          client_name: (value.cover && value.cover.client_name) || context.client_name || defaults.cover.client_name,
          matter_reference: (value.cover && value.cover.matter_reference) || context.matter_reference || defaults.cover.matter_reference,
          author: (value.cover && value.cover.author) || context.author || defaults.cover.author
        },
        header: { ...defaults.header, ...(value.header || {}) },
        footer: { ...defaults.footer, ...(value.footer || {}) },
        watermark: { ...defaults.watermark, ...(value.watermark || {}) },
        logo: { ...defaults.logo, ...(value.logo || {}) }
      }
    },
    async openDocumentDrive () {
      if (!this.result || !this.result.document) {
        this.error = 'Gere o documento antes de abrir o editor.'
        return
      }
      await this.saveGeneratedDocument()
      if (this.error) return
      this.$router.push({ name: 'documents', query: { id: this.result.document.id } })
    },
    handleFileSelect (event) {
      const file = event.target.files && event.target.files[0]
      this.setBaseFile(file)
    },
    handleFileDrop (event) {
      this.isDraggingFile = false
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      this.setBaseFile(file)
    },
    setBaseFile (file) {
      this.error = ''
      if (!file) return

      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ]
      const allowedExtensions = /\.(pdf|docx|txt)$/i

      if (!allowedTypes.includes(file.type) && !allowedExtensions.test(file.name)) {
        this.error = 'Envie um arquivo PDF, DOCX ou TXT.'
        return
      }

      this.baseFile = file
    },
    clearBaseFile () {
      this.baseFile = null
      if (this.$refs.baseFileInput) {
        this.$refs.baseFileInput.value = ''
      }
    },
    fileSize (size) {
      if (!size) return ''
      if (size < 1024 * 1024) {
        return `${Math.ceil(size / 1024)} KB`
      }
      return `${(size / (1024 * 1024)).toFixed(1)} MB`
    },
    async loadReviewers () {
      this.reviewersLoading = true
      try {
        const { data } = await api.get('/employees')
        this.employees = data
        this.selectedReviewer = this.defaultReviewer()
      } catch (error) {
        this.employees = []
      } finally {
        this.reviewersLoading = false
      }
    },
    async loadMyProfile () {
      try {
        const { data } = await api.get('/me')
        if (data && data.user) {
          this.$store.commit('auth/setUser', data.user)
          this.useProfessionalProfile = this.professionalProfileAvailable
        }
      } catch (error) {
        this.useProfessionalProfile = false
      }
    },
    defaultReviewer () {
      const active = this.employees.filter(employee => employee.access_status === 'active' && employee.user_status === 'active')
      const preferred = active.find(employee => ['legal', 'admin', 'tenant_owner', 'security_dpo'].includes(employee.role_slug))
      const reviewer = preferred || active[0]
      return reviewer ? this.reviewerValue(reviewer) : 'Jurídico'
    },
    reviewerValue (employee) {
      const name = employee.name || employee.email
      return employee.email ? `${name} <${employee.email}>` : name
    },
    async openHumanReviewDialog () {
      this.error = ''
      this.syncGeneratedEditor()
      if (!this.editorText.trim()) {
        this.error = 'Gere ou escreva o documento antes de enviar para revisão.'
        return
      }
      if (this.editorText.trim().length < 40) {
        this.error = 'O documento precisa ter pelo menos 40 caracteres para revisão humana.'
        return
      }
      if (this.result && this.result.document) {
        await this.saveGeneratedDocument()
        if (this.error) return
      }
      if (!this.employees.length && !this.reviewersLoading) {
        await this.loadReviewers()
      }
      this.selectedReviewer = this.selectedReviewer || this.defaultReviewer()
      this.reviewDialog = true
    },
    async sendToHumanReview () {
      this.syncGeneratedEditor()
      this.reviewLoading = true
      this.error = ''
      this.reviewMessage = ''

      try {
        const payload = {
          tool_name: 'Documento gerado',
          vendor_name: 'ComplyOps',
          business_purpose: `${this.form.document_type} gerado para ${this.form.destination}. ${this.form.purpose}`,
          data_categories: this.form.facts,
          legal_basis: 'Políticas internas de validação documental',
          risk_level: 'medium',
          uses_personal_data: Boolean(this.form.client_name),
          client_name: this.form.client_name,
          matter_reference: this.form.matter_reference,
          use_case_type: this.form.document_type,
          confidentiality_level: this.form.client_name ? 'confidential' : 'internal',
          expected_benefit: 'Revisar o documento antes do uso.',
          output_review_required: true,
          human_reviewer: this.selectedReviewer || 'Jurídico',
          controls: 'Revisar conteúdo, fontes, placeholders e dados sensíveis antes de liberar.',
          document_text: this.editorText,
          generated_document_id: this.result && this.result.document ? this.result.document.id : null,
          submit_now: true
        }

        const formData = new FormData()
        Object.keys(payload).forEach(key => {
          if (payload[key] !== null && payload[key] !== undefined) {
            formData.append(key, payload[key])
          }
        })

        await api.post('/human-review-requests', formData)
        await this.markGeneratedDocumentInReview()
        this.reviewMessage = 'Documento enviado para revisão humana.'
        this.reviewDialog = false
        window.setTimeout(() => {
          this.$router.push({ name: 'requests' })
        }, 700)
      } catch (error) {
        this.error = error.response && error.response.data ? error.response.data.message : 'Não foi possível enviar para revisão humana.'
      } finally {
        this.reviewLoading = false
      }
    },
    async markGeneratedDocumentInReview () {
      if (!this.result || !this.result.document) return

      try {
        const { data: updatedDocument } = await api.put(`/generated-documents/${this.result.document.id}`, {
          status: 'in_review',
          document_text: this.editorText
        })
        this.result = {
          ...this.result,
          document: updatedDocument
        }
      } catch (error) {
        console.warn('Não foi possível marcar o documento como em revisão.', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.document-generator-flow {
  display: grid;
  gap: 22px;
  align-items: stretch;
}

.generation-input-panel,
.generation-result-panel {
  display: grid;
  gap: 16px;
}

.generation-input-panel {
  width: 100%;
}

.generation-result-panel {
  width: 100%;
}

.template-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.template-option {
  min-height: 108px;
  padding: 14px;
  border: 1px solid #dde5e2;
  background: #fff;
  color: #101623;
  text-align: left;
  display: grid;
  gap: 7px;
  cursor: pointer;
}

.template-option .v-icon {
  color: #0f4efb;
}

.template-option strong,
.template-option span {
  display: block;
}

.template-option span {
  color: #60717d;
  font-size: 12px;
  line-height: 1.35;
}

.template-option--active {
  border-color: #0f4efb;
  box-shadow: inset 0 0 0 1px #0f4efb;
}

.generation-credit-estimate {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px;
  border: 1px solid #d8e2ff;
  background: #f6f9ff;
}

.generation-credit-estimate span {
  display: block;
  color: #0f4efb;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.generation-credit-estimate strong {
  display: block;
  margin-top: 4px;
  color: #101623;
  font-size: 24px;
  font-weight: 850;
}

.generation-credit-estimate p {
  max-width: 420px;
  margin: 0;
  color: #60717d;
  font-size: 13px;
  line-height: 1.45;
  text-align: right;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.professional-profile-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  padding: 14px;
  border: 1px solid #dfe8e4;
  background: #f8fbfa;
}

.professional-profile-card span,
.professional-profile-card strong,
.professional-profile-card p {
  display: block;
}

.professional-profile-card span {
  margin-bottom: 4px;
  color: #0f4efb;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.professional-profile-card strong {
  color: #101918;
}

.professional-profile-card p {
  margin: 4px 0 0;
  color: #60717d;
  line-height: 1.4;
}

.professional-profile-card .v-btn {
  grid-column: 1 / -1;
  justify-self: start;
}

.document-dropzone {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 82px;
  border: 1px dashed #9fb2ad;
  padding: 14px;
  cursor: pointer;
}

.document-dropzone .v-icon {
  color: #0f4efb;
}

.document-dropzone strong {
  display: block;
}

.document-dropzone p {
  margin: 3px 0 0;
  color: #60717d;
}

.document-dropzone-input {
  display: none;
}

.document-dropzone--dragging,
.document-dropzone--filled {
  border-color: #0f4efb;
  background: rgba(22, 86, 255, .04);
}

.result-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.generated-preview-editor {
  border: 1px solid #dde5e2;
  background: #eef2f1;
}

.generation-notes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.generation-notes article {
  border: 1px solid #dde5e2;
  padding: 14px;
  background: #fff;
}

.generation-notes ul {
  margin: 8px 0 0;
  padding-left: 18px;
  color: #60717d;
}

.result-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.dialog-helper {
  margin: 10px 0 0;
  color: #60717d;
}

@media (max-width: 760px) {
  .template-row,
  .form-grid,
  .generation-notes {
    grid-template-columns: 1fr;
  }

  .professional-profile-card {
    grid-template-columns: 1fr;
  }

  .result-actions {
    display: grid;
  }

}
</style>
